import React, { Dispatch, SetStateAction } from 'react';

import {
	differenceInCalendarDays,
	isWithinInterval,
	differenceInSeconds,
	isPast,
	differenceInYears,
} from 'date-fns';

import { FormInstance } from 'antd';

import i18n from './i18n';

import {
	Country,
	FieldData,
	IFilter,
	IFilterValues,
	InvestmentsItem,
	IUser,
	Object as ObjectType,
	ProjectPaymentSchedule,
	Questionnaire,
} from 'models';
import { QuizAnswer, QuizBody } from 'models/form';

import { OptionType, Paths, VerificationStatus, WP_HOST } from 'utils/constants';
import { getInStorage, removeFromStorage, setTimerInStorage } from './localstorage';

const { t, language } = i18n;

const DAYS = 30;
const MAX_ITEMS = 3;

export default class Utils {
	static moneyFormat(amount: number, currency: string = 'EUR') {
		const numberFormatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency,
		});

		const number = numberFormatter.format(amount);

		return number.replace(',', '');
	}

	static profitFormat(amount: number, currency: string = 'EUR') {
		const numberFormatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency,
			notation: 'compact',
			compactDisplay: 'short',
		});

		const number = numberFormatter.format(amount);

		return number.replace(',', '');
	}

	static injectFormErrors(
		fields: Array<any>,
		errors: any,
		setFields: Dispatch<SetStateAction<FieldData[]>>,
		name?: string,
	) {
		if (!errors) return;

		const newFields = fields.map((item) => {
			const name = item.name as Array<string>;
			const key = name[0];

			if (errors[key]) {
				return { ...item, errors: [t(errors[key])] };
			}
			return item;
		});

		setFields(newFields);

		if (name) {
			setFields([{ name: [name], errors: [t(errors[0].message)] }]);
		}
	}

	static generateRandomColor() {
		const color = Math.floor(Math.random() * 16777215).toString(16);
		return `#${color}`;
	}

	static formatProjectType(type: string) {
		return t(type);
	}

	static getLinkToWP(url?: string, notLang?: boolean) {
		const lang = notLang ? '' : `/${language}`;

		return `${WP_HOST}${lang}${url}`;
	}

	static getLinkLanguage(en: string, lt: string) {
		const isEn = ['en','ro'].includes(language);

		return `${WP_HOST}/${isEn ? en : lt}`;
	}

	static transformToFixedNumber(value: string, length = 2) {
		let data = value?.replace(/[^0-9,.]/g, '').replace(',', '.');
		const [int, floating] = data.split('.');
		if (floating?.length >= length) {
			return [int, floating.substring(0, length)].join(".")
		}
		return data;
	}

	static formatQuizAnswers(quiz: Questionnaire, form: ObjectType) {
		const data: QuizBody = {
			quiz_id: quiz.id,
			ver: quiz.ver,
			lang: language,
			answers: {} as QuizAnswer,
		};

		data.answers = quiz.quests.reduce((answer, item) => {
			const formOptions: any = form[item.id];

			const options = item.options
				.filter((option) => {
					return option.option_type === OptionType.TEXT
						? formOptions || true // if empty input send true
						: typeof formOptions === 'string'
						? formOptions === option.id
						: formOptions?.some((v: string) => v === option.id);
				})
				?.reduce((obj, item) => {
					const value = item.option_type === OptionType.TEXT ? formOptions || '' : true;

					return { ...obj, [item.id]: { ver: item.ver, value } };
				}, {});

			return { ...answer, [item.id]: { ver: item.ver, options } };
		}, {});

		return data;
	}

	static transformFilters(
		values: IFilterValues,
		countriesData?: Country[],
		loanTypesData?: { type: string; id: number }[],
		transactionTypesValues?: { [key: string]: string },
	) {
		Object.keys(values).forEach((key) => {
			if (!values[key as keyof IFilterValues]) {
				delete values[key as keyof IFilterValues];
			}
		});

		const otherValues = Object.keys(values)
			.map((item) => {
				const stringValue = values[item as keyof IFilterValues];

				if (typeof stringValue !== 'string' && item !== 'isPurchased') {
					return null;
				}

				const isTo = item.includes('interestRate[to]');
				const from = +values['interestRate[from]'] || 0;
				const to = +values['interestRate[to]'] || 0;

				const isToAmount = item.includes('investedAmount[to]');
				const fromAmount = +(values['investedAmount[from]'] || 0);
				const toAmount = +(values['investedAmount[to]'] || 0);

				if (isTo && to < from) {
					return null;
				}

				if (isToAmount && toAmount < fromAmount) {
					return null;
				}

				const isFrom = item.includes('from');

				const interestRateText = t('interestRateText');
				const investedAmountText = t('investedAmountText');
				const projectIdText = t('projectIdText');
				const fromText = t('fromText');
				const toText = t('toText');
				const excludeInvestmentsText = t('excludeInvestmentsText');
				const withReducedPriceText = t('withReducedPriceText');
				const filterNominalValueText = t('filterNominalValueText');
				const rangeText = isFrom ? fromText : toText;

				let value;

				switch (item) {
					case 'excludeMyInvestment':
						value = excludeInvestmentsText;
						break;
					case 'reducedPriceOnly':
						value = withReducedPriceText;
						break;
					case 'onlyAtNominalValue':
						value = filterNominalValueText;
						break;
					case 'onlyMySales':
						value = t('mySalesText');
						break;
					case 'loanId':
						value = `${projectIdText} ${stringValue}`;
						break;
					case 'projectTitle':
						value = stringValue;
						break;
					case 'originatorTitle':
					case 'companyName':
						value = stringValue;
						break;
					case 'ltv[from]':
					case 'ltv[to]':
						value = `LTV ${rangeText} ${stringValue}%`;
						break;
					case 'interestRate[from]':
					case 'interestRate[to]':
						value = `${interestRateText} ${rangeText} ${stringValue}%`;
						break;
					case 'investedAmount[from]':
					case 'investedAmount[to]':
						value = `${investedAmountText} ${rangeText} ${Utils.moneyFormat(Number(stringValue))}`;
						break;
					case 'isPurchased':
						value = t(stringValue === '0' ? 'primaryMarketText' : 'secondaryMarketTitleText');
						break;
					case 'minDateOfPurchase':
						value = `${fromText} ${stringValue}`;
						break;
					case 'maxDateOfPurchase':
						value = `${toText} ${stringValue}`;
						break;
					case 'overdueInterestPayments':
						value = t('overdueInterestText');
						break;
					case 'lateLoans':
						value = t('lateLoansText');
						break;
					default:
						value = stringValue;
						break;
				}

				return {
					name: item,
					value,
					fieldName: item,
				};
			})
			.filter(Boolean) as IFilter[];

		const types = (values.type ?? []).map((item: string) => {
			return {
				name: item,
				value: Utils.formatProjectType(item),
				fieldName: 'type',
			};
		});

		const riskLvls = (values.riskLvl ?? []).map((item: string) => {
			return {
				name: item,
				value: Utils.formatProjectType(item),
				fieldName: 'riskLvl',
			};
		});

		const transactionsTypes = (values.transactionType ?? []).map((item: string) => {
			return {
				name: item,
				value: transactionTypesValues ? transactionTypesValues[item] : '',
				fieldName: 'transactionType',
			};
		});

		const loanTypes =
			loanTypesData
				?.filter((item) => {
					return values.loanTermId?.some((v: string) => +v === +item.id);
				})
				.map((item) => {
					return {
						name: item.id,
						value: Utils.formatProjectType(item.type),
						fieldName: 'loanTermId',
					};
				}) || [];

		const countries =
			countriesData
				?.filter((item) => {
					return values.country?.some((v: string) => +v === +item.id);
				})
				.map((item: Country) => {
					return {
						name: item.id,
						value: item.title,
						fieldName: 'country',
					};
				}) || [];

		return [
			...types,
			...riskLvls,
			...loanTypes,
			...countries,
			...otherValues,
			...transactionsTypes,
		];
	}

	static copyText(text: string) {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(text);
		} else {
			const unsecuredCopyToClipboard = (text: string) => {
				const textArea = document.createElement('textarea');
				textArea.value = text;
				document.body.appendChild(textArea);
				textArea.focus();
				textArea.select();
				try {
					document.execCommand('copy');
				} catch (err) {}
				document.body.removeChild(textArea);
			};

			unsecuredCopyToClipboard(text);
		}
	}

	static transformToRangeNumber(value: number, min: number, max: number) {
		if (value > max) {
			return max;
		}
		if (value < min) {
			return min;
		}

		return value;
	}

	static reverseMatrix(matrix: ProjectPaymentSchedule[][]) {
		const reversedMatrix = [...matrix];

		reversedMatrix.reverse();

		return reversedMatrix;
	}

	static isDocumentNull = (documents: { expired: 0 | 1; valid_until: string }[]) => {
		return !documents?.some((item) => item.expired !== null);
	};

	static isDocumentActive = (documents: { expired: 0 | 1; valid_until: string }[]) => {
		return documents?.find((item) => {
			return item.expired === 0;
		});
	};

	static isDocumentExpiredSoon = (documents: { expired: 0 | 1; valid_until: string }[]) => {
		const isActiveDocuments = documents?.find(
			(item) =>
				item.expired === 0 &&
				differenceInCalendarDays(new Date(item.valid_until), new Date()) > DAYS,
		);

		if (isActiveDocuments) {
			return false;
		}

		return documents?.find(
			(item) =>
				item.expired === 0 &&
				differenceInCalendarDays(new Date(item.valid_until), new Date()) < DAYS,
		);
	};

	static isDateInDecember(dateToCheck: Date) {
		const startOfDecember = new Date(dateToCheck.getFullYear(), 11, 20);
		const endOfDecember = new Date(dateToCheck.getFullYear(), 11, 31, 23, 59, 59, 999);

		return isWithinInterval(dateToCheck, { start: startOfDecember, end: endOfDecember });
	}

	static formatTime = (seconds: number) => {
		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		const secs = seconds % 60;

		const formattedHours = hours < 10 ? `0${hours}` : hours;
		const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
		const formattedSeconds = secs < 10 ? `0${secs}` : secs;

		return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
	};

	static setTimerInStorage = ({
		storageName,
		seconds,
		setSeconds,
		onStartTimer,
	}: {
		storageName: string;
		seconds: number;
		setSeconds: (v: number) => void;
		onStartTimer: () => void;
	}) => {
		const timerStorage = getInStorage(storageName);
		if (timerStorage) {
			const timer = timerStorage as string;
			const date = new Date(timer);
			if (isPast(date)) {
				removeFromStorage(storageName);
			} else {
				setSeconds(differenceInSeconds(date, new Date()));
				onStartTimer();
			}
		} else {
			setTimerInStorage(seconds, storageName);
			onStartTimer();
			setSeconds(seconds);
		}
	};

	static fundingRedirect = (user: IUser) => {
		if (!user.taxResidence) {
			return Paths.RESIDENCY;
		}

		if (
			(user.verificationStatus === VerificationStatus.VERIFIED && !user.riskTolerance?.value) ||
			(user.verificationStatus === VerificationStatus.VERIFIED &&
				!Utils.checkYearsDifferent(user.riskTolerance?.date || '', 2))
		) {
			return Paths.OVERVIEW;
		}

		return user.verificationStatus === VerificationStatus.FAILED
			? Paths.VERIFY_IDENTITY_FAILED
			: Paths.VERIFY_IDENTITY;
	};

	static transformValues = (values: InvestmentsItem[]) => {
		if (values.length <= MAX_ITEMS + 1) return values;

		const displayedValues = values.slice(0, MAX_ITEMS);
		if (values.length > MAX_ITEMS + 1) {
			const otherSum = values.slice(MAX_ITEMS).reduce((sum, item) => sum + +item.amount, 0);
			const otherPart = Number(
				100 - displayedValues.reduce((sum, item) => sum + +item.part, 0),
			).toFixed(2);

			displayedValues.push({ type: t('otherText'), amount: otherSum, part: `${otherPart}` });
		}
		return displayedValues;
	};

	static removeFractionalPartIfZeroes(num: string) {
		if (num.includes('.')) {
			const [integerPart, fractionalPart] = num.split('.');

			if (/^0*$/.test(fractionalPart)) {
				return Number(integerPart);
			}

			return num;
		}

		return num;
	}

	static debounce(func: (v: any) => void, timeout = 300) {
		let timer: string | number | NodeJS.Timeout | undefined;

		return (...args: any) => {
			clearTimeout(timer);
			timer = setTimeout(() => {
				func.apply(this, args);
			}, timeout);
		};
	}

	static scrollToFirstError = (errors: { name: string }[], form: FormInstance) => {
		form.scrollToField(Object.keys(errors)[0], {
			behavior: 'smooth',
			block: 'center',
		});
	};

	static getHighlightedText(text: string, highlight: string) {
		const highlightWords = highlight.split(/\s+/).filter(Boolean);

		const words = text.split(/\s+/);

		const highlightWord = (word: string) => {
			let highlighted = word;

			highlightWords.forEach((hw) => {
				const regex = new RegExp(`(^|[„"'«])(${hw})`, 'gi');
				highlighted = highlighted.replace(
					regex,
					(match) => `<span class='highlightWord'>${match}</span>`,
				);
			});
			return highlighted;
		};

		const highlightedText = words.map((word, i) => (
			<React.Fragment key={i}>
				{i > 0 && ' '}
				<span dangerouslySetInnerHTML={{ __html: highlightWord(word) }} />
			</React.Fragment>
		));

		return <>{highlightedText}</>;
	}

	static safeParseJson(jsonString: string) {
		try {
			return JSON.parse(jsonString);
		} catch (error) {
			return '';
		}
	}

	static checkYearsDifferent(value: string, years = 2) {
		const now = new Date();
		const yearsDiff = differenceInYears(now, new Date(value));

		return yearsDiff < years;
	}
}
